// Core Libraries
import React from 'react'
import PropTypes from 'prop-types'
import { get, isNil } from 'lodash'
import { Alert, Heading, DetailList, LoadingSpinner } from '@ideon-api/ideon-ui'

// Helpers
import client from '../../client'

// Components
import Component from '../common/Component'

// PD-30386: "what this will queue" -- the counts an operator needs before
// turning sbc_parser_v2 on for an issuer status. Advisory only: it reports what
// Howie::Query::SbcParse::Eligibility would match, and never gates the save.
//
// Built with ideon-ui, matching the wireframe's own chrome. The .ideon-ui-scope
// wrapper below is what supplies the floor ideon-ui assumes -- see
// docs/knowledge/known-issues.md #20 and ADR 0011.
//
// It is not an EditForm field, so it takes `attributes` -- which Form hands
// every child -- purely to learn the record id.
export default class SbcParserV2Panel extends Component {
  static propTypes = {
    attributes: PropTypes.shape({ id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }),
    className: PropTypes.string
  }

  constructor(props) {
    super(props)

    this.state = { summary: null, failed: false }
  }

  componentDidMount() {
    this.load()
  }

  componentDidUpdate(previousProps) {
    if (this.id !== get(previousProps, 'attributes.id')) this.load()
  }

  get id() {
    return get(this.props, 'attributes.id')
  }

  // The years the fork will actually route, which is not the issuer status's
  // raw period: eligibility floors at a minimum plan year, so a status active
  // since 2014 still queues nothing before that. The server sends the floored
  // range precisely so this does not have to know the floor.
  get planYearsInScope() {
    const { first_plan_year: first, last_plan_year: last } = this.state.summary

    if (isNil(first)) return '—'

    return isNil(last) ? `${first} onward` : `${first} to ${last}`
  }

  get rows() {
    const summary = this.state.summary

    return [
      ['Plan years in scope', this.planYearsInScope],
      ['Plans in scope', summary.plans_in_scope],
      ['SBCs awaiting parse', summary.sbcs_awaiting_parse],
      ['Plans with no SBC', summary.plans_without_sbc]
    ]
  }

  get body() {
    if (this.state.failed) {
      return <p className="text-sm">Could not load the eligibility counts.</p>
    }

    if (isNil(this.state.summary)) return <LoadingSpinner message="Loading the eligibility counts…" />

    return (
      <DetailList.Root>
        {this.rows.map(([label, value]) => (
          <DetailList.Item
            key={label}
            label={label}
            value={value}
            data-testid={`sbc-parser-v2-${label.toLowerCase().replace(/\s+/g, '-')}`}
          />
        ))}
      </DetailList.Root>
    )
  }

  // Both callbacks are guarded on the id they were issued for. componentDidUpdate
  // starts a new request whenever the id changes, and nothing cancels the old
  // one -- so a slow response for the previous record can settle after the new
  // one and paint another issuer status's counts, or its failure message, into
  // a panel that has already moved on. Neither would look like an error; the
  // numbers would just be wrong. The guard drops a response whose id is no
  // longer the one on screen.
  load() {
    const requestedId = this.id

    if (isNil(requestedId)) return

    this.setState({ summary: null, failed: false })

    client
      .get(`/issuer_statuses/${requestedId}/sbc_parse_eligibility`)
      .then((response) => {
        if (requestedId !== this.id) return
        this.setState({ summary: response.data })
      })
      .catch(() => {
        if (requestedId !== this.id) return
        this.setState({ failed: true })
      })
  }

  render() {
    if (isNil(this.id)) return null

    return (
      // bg-white/dark:bg-ideon-gray-95 override the "info" variant's tinted
      // background (Alert.tsx: bg-ideon-blue-20/10) so the panel matches the
      // solid ground DetailList.Item's label/value chips are hardcoded to
      // mask their dashed leader line against (that same bg-white/dark:bg-
      // ideon-gray-95 pair) -- without this the chips read as white boxes on
      // the tinted panel instead of disappearing behind the label text.
      <Alert
        className={`ideon-ui-scope mt-4 bg-white dark:bg-ideon-gray-95 ${this.props.className || ''}`}
        id="sbc_parser_v2_panel"
      >
        {/* Alert sizes its icon (h-4 w-4) for its own text-sm body copy, not
          * for a Heading -- title-m renders at text-[1.375rem], far larger
          * than the icon, which throws off Alert's `items-start` alignment.
          * Overriding the size/leading utilities (cva + tailwind-merge lets
          * a later className win) brings the title down to the icon's own
          * scale; font-semibold from the variant is kept. */}
        <Heading variant="title-m" className="text-sm leading-5">
          Saving this on makes these SBCs eligible for the next batch
        </Heading>
        <p className="mt-1 text-sm">
          Every SBC attached to a plan under this issuer status becomes eligible, for every
          plan year inside the issuer status&apos;s own period, and joins the next batch.
          Nothing reaches a plan until a reviewer approves it.
        </p>
        <div className="mt-3">{this.body}</div>
      </Alert>
    )
  }
}
